import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useLocation, useLoaderData, useActionData, useSubmit } from 'react-router';

import Page from 'client/components/aria/Page/Page';
import Search from 'client/components/aria/Search/Search';
import SortButton from 'client/components/aria/SortButton/SortButton';
import ShowResults from 'client/components/aria/ShowResults/ShowResults';
import DeleteDialog from 'client/components/aria/Dialog/DeleteDialog/DeleteDialog';
import ShareDialog from 'client/components/aria/Dialog/ShareDialog/ShareDialog';
import Table from 'client/components/aria/Table';
import { Cell, Column, Item, Row, TableBody, TableHeader } from 'react-stately';
import Menu from 'client/components/aria/Menu/Menu';
import Pagination from 'client/components/aria/Pagination/Pagination';
import Button from 'client/components/aria/Button/Button';
import Alert from 'client/components/aria/Alert/Alert';
import NavLink from 'client/components/aria/NavLink/NavLink';

import moment from 'moment';
import Icon from '@mdi/react';
import { mdiDelete, mdiDotsVertical } from '@mdi/js';
import { BOOKMARKS_PAGE, MAP_PAGE } from 'client/utils/routes/routeConstants';

import styles from './BookmarkPage.module.scss';
import { useClearAOI } from 'client/pages/MapPage/hooks/clearAOI';
import { VisuallyHidden } from 'react-aria';

const VIEW = 'view';
const SHARE = 'share';
const DELETE = 'delete';

const BookmarkPage = () => {
    const intl = useIntl();
    const loaderData = useLoaderData();
    const actionData = useActionData();
    const { content, totalPages } = loaderData.bookmarks;
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const currentPage = parseInt(params.get('page')) + 1 || 1;
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [shareBookmark, setShareBookmark] = useState();
    const [deletionDetails, setDeletionDetails] = useState({});
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const clearAOI = useClearAOI();
    const submit = useSubmit();

    useEffect(() => {
        // Resets all temporary state when the content changes, such as search
        setDeletionDetails({});
        setSelectedKeys(new Set());
        setShareBookmark(undefined);
    }, [content]);

    const onDelete = () => {
        const formData = new FormData();
        formData.set('bookmarkIds', JSON.stringify(deletionDetails.ids));
        submit(formData, {
            method: 'DELETE',
            action: BOOKMARKS_PAGE
        });
    };

    const handleBookmarkView = (bookmarkId) => {
        clearAOI();
        navigate(MAP_PAGE + '?bookmark=' + bookmarkId);
    };

    const onTableMenuAction = (evt, bookmark) => {
        const { bookmarkId } = bookmark;
        if (evt === VIEW) {
            handleBookmarkView(bookmarkId);
        } else if (evt === SHARE) {
            setShareBookmark(bookmark);
            setIsShareOpen(true);
        } else if (evt === DELETE) {
            setDeletionDetails({ title: bookmark.name, ids: [bookmark.bookmarkId] });
            setIsDeleteOpen(true);
        }
    };

    const onPaginationChange = page => {
        const params = new URLSearchParams(location?.search);
        params.set('page', page - 1);
        navigate(location?.pathname + '?' + params.toString());
    };

    const isItemsSelected = selectedKeys.size > 0 || selectedKeys === 'all';

    const onDeletePress = () => {
        if (!isItemsSelected) {
            return;
        }
        const ids = (selectedKeys === 'all') ? content.map(b => b.bookmarkId) : Array.from(selectedKeys);
        let title;
        if (ids.length === 1) {
            title = content.find(b => b.bookmarkId === ids[0]).name;
        } else {
            title = ids.length + ' ' + intl.formatMessage({ id: 'bookmarkPage.items' });
        }
        setDeletionDetails({
            title,
            ids
        });
        setIsDeleteOpen(true);
    };

    return (
        <Page title={intl.formatMessage({ id: 'bookmarkPage.title' })} role="main" data-testid="bookmarkPage"
            className={styles.page}>
            {actionData?.deleted
                ? <div className="govuk-grid-row">
                    <div className="govuk-grid-column-full govuk-!-margin-bottom-6">
                        <Alert data-testid="bookmarkPage.delete.confirmation" variant="warning" title={intl.formatMessage({ id: 'bookmarkPage.title' })}>
                            <h2 className="govuk-error-summary__title govuk-!-margin-0">
                                {intl.formatMessage({ id: 'bookmarkPage.bookmarksDeleted' })}
                            </h2>
                        </Alert>
                    </div>
                </div>
                : null
            }
            <div className="govuk-grid-row">
                <div className="govuk-grid-column-full">
                    <h1 className={['govuk-heading-xl', styles.header].join(' ')}>{intl.formatMessage({ id: 'bookmarkPage.title' })}</h1>
                    <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible"/>
                </div>
            </div>
            <div className="govuk-grid-row">
                <div className="govuk-grid-column-full">
                    <Search
                        data-testid='bookmarkpage'
                        totalElements={loaderData.bookmarks.totalElements}
                        size={loaderData.bookmarks.size}
                    />
                    {isItemsSelected &&
                        <div className={'govuk-grid-column-one-half'}>
                            <Button
                                data-testid={'delete'}
                                variant={'error'}
                                onPress={onDeletePress}
                            >
                                <Icon path={mdiDelete} size={0.7}/>
                                {' '}
                                {intl.formatMessage({ id: 'delete' })}
                            </Button>
                        </div>
                    }
                </div>
            </div>
            <div className={'govuk-grid-row'}>
                <div className={['govuk-grid-column-full', styles.table].join(' ')}>
                    <Table
                        aria-label={intl.formatMessage({ id: 'bookmarkPage.title' })}
                        selectionMode="multiple"
                        data-testid="myBookmarks"
                        selectedKeys={selectedKeys}
                        onSelectionChange={setSelectedKeys}
                    >
                        <TableHeader>
                            <Column>
                                <SortButton column={'name'}>
                                    {intl.formatMessage({ id: 'bookmarkPage.name' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'type'}>
                                    {intl.formatMessage({ id: 'bookmarkPage.type' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'dateLastUpdated'}>
                                    {intl.formatMessage({ id: 'bookmarkPage.dateModified' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <VisuallyHidden><FormattedMessage id="menu" /></VisuallyHidden>
                            </Column>
                        </TableHeader>
                        <TableBody>
                            {content && content.map(bookmark => <Row key={bookmark.bookmarkId}>
                                <Cell wrapText><NavLink data-testid='myBookmarks.link' to={MAP_PAGE + '?bookmark=' + bookmark.bookmarkId} onClick={() => clearAOI()}>{bookmark.name}</NavLink></Cell>
                                <Cell>{bookmark.type}</Cell>
                                <Cell>{moment(bookmark.dateLastUpdated).format('lll')}</Cell>
                                <Cell horzAlign="middle">
                                    <Menu
                                        aria-label={bookmark.name + ' ' + intl.formatMessage({ id: 'menu' })}
                                        icon={<Icon path={mdiDotsVertical} size={1}/>}
                                        onAction={evt => onTableMenuAction(evt, bookmark)}
                                        placement="bottom right"
                                        data-testid={'myBookmarks.dropdownMenu'}
                                    >
                                        <Item
                                            key={VIEW}
                                            aria-label={intl.formatMessage({ id: 'view' }) + ' ' + bookmark.name}
                                        >
                                            <span data-testid={'myBookmarks.dropdownMenu.view'}>
                                                {intl.formatMessage({ id: 'bookmarkPage.viewBookmark' })}
                                            </span>
                                        </Item>
                                        <Item
                                            key={DELETE}
                                            aria-label={intl.formatMessage({ id: 'delete' }) + ' ' + bookmark.name}
                                        >
                                            <span data-testid={'myBookmarks.dropdownMenu.delete'}>
                                                {intl.formatMessage({ id: 'delete' })}
                                            </span>
                                        </Item>
                                        <Item
                                            key={SHARE}
                                            aria-label={intl.formatMessage({ id: 'bookmarkPage.share' }) + ' ' + bookmark.name}
                                        >
                                            <span data-testid={'myBookmarks.dropdownMenu.share'}>
                                                {intl.formatMessage({ id: 'bookmarkPage.share' })}
                                            </span>
                                        </Item>
                                    </Menu>
                                </Cell>
                            </Row>)}
                        </TableBody>
                    </Table>
                </div>
            </div>
            <div className="govuk-grid-row">
                <div className={['govuk-grid-column-full', styles.pagination].join(' ')}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={onPaginationChange}
                        data-testid="pagination"
                    />
                </div>
                <div className="govuk-grid-column-full govuk-!-text-align-centre">
                    <ShowResults
                        totalElements={loaderData.bookmarks.totalElements}
                        size={loaderData.bookmarks.size}
                    />
                </div>
            </div>

            <ShareDialog
                isOpen={isShareOpen}
                setIsOpen={setIsShareOpen}
                title={shareBookmark?.name}
                url={window.location.origin + MAP_PAGE + '?bookmark=' + shareBookmark?.bookmarkId}
                restrictedLink
            />

            <DeleteDialog
                data-testid={'bookmark.deleteDialog'}
                isOpen={isDeleteOpen}
                setIsOpen={setIsDeleteOpen}
                onDelete={onDelete}
                title={deletionDetails.title}
            />
        </Page>
    );
};

export default BookmarkPage;
